/* eslint-disable import/no-unresolved */
import React, { useState, useEffect } from 'react';
import { debounce } from 'lodash-es';
import shortid from 'shortid';

import DevopsSVG from 'assets/resources/devops.inline.svg';
import FrontendSVG from 'assets/resources/frontend.inline.svg';
import BackendSVG from 'assets/resources/backend.inline.svg';
import MobileSVG from 'assets/resources/mobile.inline.svg';
import InfrastructureSVG from 'assets/resources/hardware.inline.svg';

import frontendIcons from './constants/frontend-icons';
import backendIcons from './constants/backend-icons';
import nativeIcons from './constants/native-icons';
import crossPlatformIcons from './constants/cross-platform-icons';
import infrastructureIcons from './constants/infrastructure-icons';
import devOpsIcons from './constants/dev-ops-icons';

import ImgHTML from '../../ImgHTML';
import LazyLoad from '../../LazyLoad';

function getDistX(a, b) {
  if (a === null || b === null) return 0;
  const { x: xA } = a.getBoundingClientRect();
  const { x: xB } = b.getBoundingClientRect();
  return xB - xA;
}
function getDistY(a, b) {
  if (a === null || b === null) return 0;
  const { top: topA } = a.getBoundingClientRect();
  const { top: topB } = b.getBoundingClientRect();
  return topB - topA;
}

const Technologies = ({ subtitle, extraText, greyBg }) => {
  const [state, setState] = useState({
    distMobileBackendY: 0,
    distDevopsBackendY: 0,
    distFrontendBackendY: 0
  });
  const handleResize = () => {
    const frontend = document.getElementById('frontend-tech');
    const backend = document.getElementById('backend-tech');
    const mobile = document.getElementById('mobile-tech');
    const devops = document.getElementById('devops-tech');
    const offsetX = 30;
    const offsetY = 60;
    const distFrontendBackendY = getDistY(frontend, backend) + offsetY;
    const distMobileBackendX = getDistX(mobile, backend) + offsetX;
    const distMobileBackendY = getDistY(mobile, backend) + offsetY;
    const distDevopsBackendY = getDistY(devops, backend) + offsetY;

    setState({
      distMobileBackendY,
      distMobileBackendX,
      distDevopsBackendY,
      distFrontendBackendY
    });
  };
  useEffect(() => {
    window.addEventListener('resize', debounce(handleResize, 500));
    handleResize();
    return () => {
      window.removeEventListener('resize', debounce(handleResize, 500));
    };
  }, []);

  return (
    <div className="technologies" id="technologies">
      <div className="container">
        <div className="subtitle without bigger">
          {subtitle}
          <div className="extra">{extraText}</div>
        </div>
        <div className="technologies-container">
          <div className="box" id="frontend-tech">
            <svg className="lineContainer ">
              <polyline
                points={`0,50 -10,50 -10,${state.distFrontendBackendY} 0,${state.distFrontendBackendY}`}
              />
              <polyline
                className="redline"
                points={`-15,${(state.distFrontendBackendY * 4) / 5} -15,${
                  state.distFrontendBackendY
                }`}
              />
            </svg>
            <span className={`title ${greyBg ? 'with-bg' : ''}`}>
              <div className="dot first" />
              Frontend
            </span>
            <div className="left-big-icon">
              <FrontendSVG />
            </div>
            <div className="right-icons">
              {frontendIcons.map(icon => (
                <div key={shortid.generate()} className="icon-wrapper">
                  <LazyLoad>
                    <ImgHTML
                      src={icon.src}
                      className={icon.nameClass}
                      alt={icon.alt}
                    />
                  </LazyLoad>
                </div>
              ))}
            </div>
          </div>
          <div className="box mobile" id="mobile-tech">
            <svg className="lineContainer right mobile">
              <polyline
                points={`1,50 11,50 10,${state.distMobileBackendY} 1,${state.distMobileBackendY}`}
              />
              <polyline
                className="redline"
                points={`15,${state.distMobileBackendY / 2} 15,${
                  state.distMobileBackendY
                }`}
              />
            </svg>
            <svg className="lineContainer mobile-large">
              <polyline points={`0,20 ${state.distMobileBackendX},20`} />
              <polyline
                className="redline"
                points={`-3,25 ${state.distMobileBackendX / 8},25`}
              />
            </svg>
            <span className={`title ${greyBg ? 'with-bg' : ''}`}>
              <div className="dot second" />
              Mobile
            </span>
            <div className="left-big-icon">
              <MobileSVG />
            </div>
            <div className="right-icons">
              {[...nativeIcons, ...crossPlatformIcons].map(icon => (
                <div key={shortid.generate()} className="icon-wrapper">
                  <LazyLoad>
                    <ImgHTML
                      src={icon.src}
                      className={icon.nameClass}
                      alt={icon.alt}
                    />
                  </LazyLoad>
                </div>
              ))}
            </div>
          </div>
          <div className="box" id="backend-tech">
            <span className={`title ${greyBg ? 'with-bg' : ''}`}>
              <div className="dot third" />
              Backend
            </span>
            <div className="left-big-icon">
              <BackendSVG />
            </div>
            <div className="right-icons">
              {backendIcons.map(icon => (
                <div key={shortid.generate()} className="icon-wrapper">
                  <LazyLoad>
                    <ImgHTML
                      src={icon.src}
                      className={icon.nameClass}
                      alt={icon.alt}
                    />
                  </LazyLoad>
                </div>
              ))}
            </div>
          </div>
          <div className="box" id="devops-tech">
            <svg className="lineContainer right devops">
              <polyline
                points={`1,50 10,50 10,${state.distDevopsBackendY} 1,${state.distDevopsBackendY}`}
              />
              <polyline
                className="redline"
                points={`15,${state.distDevopsBackendY / 2} 15,${
                  state.distDevopsBackendY
                }`}
              />
            </svg>
            <span className={`title ${greyBg ? 'with-bg' : ''}`}>
              <div className="dot" />
              DevOps
            </span>
            <div className="left-big-icon">
              <DevopsSVG />
            </div>
            <div className="right-icons">
              {devOpsIcons.map(icon => (
                <div key={shortid.generate()} className="icon-wrapper">
                  <LazyLoad>
                    <ImgHTML
                      src={icon.src}
                      className={icon.nameClass}
                      alt={icon.alt}
                    />
                  </LazyLoad>
                </div>
              ))}
            </div>
          </div>
          <div className="box">
            <span className={`title ${greyBg ? 'with-bg' : ''}`}>
              <div className="dot" />
              Infrastructure
            </span>
            <div className="left-big-icon">
              <InfrastructureSVG />
            </div>
            <div className="right-icons">
              {infrastructureIcons.map(icon => (
                <div key={shortid.generate()} className="icon-wrapper">
                  <LazyLoad>
                    <ImgHTML
                      src={icon.src}
                      className={icon.nameClass}
                      alt={icon.alt}
                    />
                  </LazyLoad>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Technologies;
